const VehicleFilterForm = () => {
  const core = (window as any).EcommerceCore;
  const React = core.vendors.React;
  const { useMemo } = React;
  const { useForm, Controller } = core.vendors.ReactHookForm;
  const Select = core.vendors.ReactSelect.default || core.vendors.ReactSelect;
  const { useGetCategoriesQuery, useGetVehiclesQuery } =
    core.product.api.taxonomies;
  const { getVehicleModelOptions } = core.product.helpers;
  const Icon = core.common.components.Icon;

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: { category: null, brand: null, model: null },
  });
  const {
    register: registerStockCode,
    handleSubmit: handleStockSubmit,
    watch: watchStockCode,
  } = useForm({
    defaultValues: { stockCode: "" },
  });
  const category = watch("category");
  const brand = watch("brand");
  const stockCode = watchStockCode("stockCode").trim();
  const { data: categoryTree = [], isLoading: categoriesLoading } =
    useGetCategoriesQuery();
  const {
    currentData: vehicles = [],
    isFetching: vehiclesLoading,
    isError,
  } = useGetVehiclesQuery(category ? [category] : []);

  const categories = useMemo(() => {
    const options: { value: number | null; label: string }[] = [
      { value: null, label: "Tüm ürünler" },
    ];
    const visit = (nodes: any[], prefix: string[]) => {
      nodes.forEach((node) => {
        const path = [...prefix, node.name];
        options.push({ value: node.id, label: path.join(" / ") });
        visit(node.children || [], path);
      });
    };
    visit(categoryTree, []);
    return options;
  }, [categoryTree]);
  const brands = vehicles.map((node: any) => ({
    value: node.id,
    label: node.name,
  }));
  const models = getVehicleModelOptions(
    vehicles.find((node: any) => node.id === brand),
  );

  const getShopUrl = () => {
    const url = new URL(
      (window as any).commonVariables?.shopUrl || "/shop/",
      window.location.origin,
    );
    const language = new URLSearchParams(window.location.search).get("lang");
    if (language) url.searchParams.set("lang", language);
    return url;
  };

  const onVehicleSubmit = (data: any) => {
    const url = getShopUrl();
    if (data.category)
      url.searchParams.set("productCategory", String(data.category));
    const vehicle = data.model || data.brand;
    if (vehicle) url.searchParams.set("productVehicle", String(vehicle));
    window.location.href = url.toString();
  };

  const onStockSubmit = (data: any) => {
    const code = (data.stockCode || "").trim();
    if (!code) return;
    const url = getShopUrl();
    url.searchParams.set("productSearch", code);
    url.searchParams.set("productCodeSearch", "true");
    window.location.href = url.toString();
  };

  const fields = [
    {
      name: "category",
      label: "ÜRÜN GRUBU",
      options: categories,
      placeholder: "Tüm ürünler",
      disabled: false,
      loading: categoriesLoading,
    },
    {
      name: "brand",
      label: "ARAÇ MARKASI",
      options: brands,
      placeholder: "Araç markası seçiniz",
      disabled: vehiclesLoading || isError,
      loading: vehiclesLoading,
    },
    {
      name: "model",
      label: "ARAÇ MODELİ",
      options: models,
      placeholder: "Tüm modeller",
      disabled: !brand || models.length === 0 || vehiclesLoading || isError,
      loading: false,
    },
  ];

  return (
    <div className="vfb-search">
      <form
        className="vfb-form"
        aria-label="Araca göre ürün arama"
        onSubmit={handleSubmit(onVehicleSubmit)}
      >
        {fields.map((config, index) => (
          <div className="vfb-form__group" key={config.name}>
            <label className="vfb-form__label" htmlFor={`vfb-${config.name}`}>
              {config.label}
            </label>
            <div className="vfb-form__input-wrapper">
              <span className="vfb-form__step">{index + 1}</span>
              <Controller
                name={config.name}
                control={control}
                render={({ field }: any) => (
                  <Select
                    {...field}
                    inputId={`vfb-${config.name}`}
                    instanceId={`vfb-${config.name}`}
                    classNamePrefix="vfb-select"
                    options={config.options}
                    placeholder={config.placeholder}
                    isSearchable
                    isClearable={config.name !== "category"}
                    isLoading={config.loading}
                    isDisabled={config.disabled}
                    noOptionsMessage={() => "Uygun seçenek bulunamadı"}
                    value={
                      config.options.find(
                        (option: any) => option.value === field.value,
                      ) || null
                    }
                    onChange={(option: any) => {
                      field.onChange(option?.value ?? null);
                      if (config.name === "category") setValue("brand", null);
                      if (config.name !== "model") setValue("model", null);
                    }}
                  />
                )}
              />
            </div>
          </div>
        ))}
        <div className="vfb-form__group vfb-form__group--submit">
          <button
            type="submit"
            className="vfb-form__submit-btn"
            disabled={categoriesLoading || vehiclesLoading}
          >
            <Icon iconName="search" />
            <span>ARA</span>
          </button>
        </div>
        {isError && (
          <p className="vfb-form__message" role="alert">
            Araç seçenekleri yüklenemedi. Ürün grubuna göre arama
            yapabilirsiniz.
          </p>
        )}
        {!vehiclesLoading && !isError && category && vehicles.length === 0 && (
          <p className="vfb-form__message">
            Bu ürün grubunda araç uyumluluğu tanımlanmış ürün bulunamadı.
          </p>
        )}
      </form>
      <div className="vfb-search__separator">
        <span>VEYA</span>
      </div>
      <form
        className="vfb-form vfb-form--stock"
        aria-label="Ürün veya referans koduyla arama"
        onSubmit={handleStockSubmit(onStockSubmit)}
      >
        <div className="vfb-form__group">
          <label className="vfb-form__label" htmlFor="vfb-stock-code">
            Ürün veya referans koduyla ara
          </label>
          <input
            {...registerStockCode("stockCode")}
            id="vfb-stock-code"
            className="vfb-form__input"
            type="search"
            placeholder="Örn. FY-VW 0001, 071115562A, WY 106"
            aria-describedby="vfb-code-help"
            autoComplete="off"
          />
          <p id="vfb-code-help" className="vfb-form__code-help">
            Ürün, OEM, FİL FİLTRE, WUNDER veya MANN kodu giriniz.
          </p>
        </div>
        <button
          type="submit"
          className="vfb-form__submit-btn"
          disabled={!stockCode}
        >
          <Icon iconName="search" />
          <span>ARA</span>
        </button>
      </form>
    </div>
  );
};

export default VehicleFilterForm;
